import { useState } from "react";

const questions = [
  {
    id: 1,
    letter: "S",
    text: "Before reacting to a difficult situation, I pause and assess before responding.",
  },
  {
    id: 2,
    letter: "S",
    text: "I can sit with discomfort or uncertainty without needing to immediately act or speak.",
  },
  {
    id: 3,
    letter: "I",
    text: "When I'm frustrated or angry, I am able to think strategically rather than react emotionally.",
  },
  {
    id: 4,
    letter: "I",
    text: "I choose my words and actions based on the outcome I want, not the emotion I feel.",
  },
  {
    id: 5,
    letter: "G",
    text: "I consistently recognize and appreciate small wins, small gifts, and small moments of grace.",
  },
  {
    id: 6,
    letter: "G",
    text: "I treat every opportunity — no matter how minor — as something worth honoring and stewarding well.",
  },
  {
    id: 7,
    letter: "N",
    text: "I can tell the difference between a door that is opening for me and one I am forcing open.",
  },
  {
    id: 8,
    letter: "N",
    text: "When something consistently resists me despite real effort, I reassess direction rather than just pushing harder.",
  },
  {
    id: 9,
    letter: "A",
    text: "The work I am doing right now feels like it is moving with momentum rather than against resistance.",
  },
  {
    id: 10,
    letter: "A",
    text: "I am investing my energy in purposes bigger than my personal ego or immediate gain.",
  },
  {
    id: 11,
    letter: "L",
    text: "I am consistently living in a way that I would be proud for others to model.",
  },
  {
    id: 12,
    letter: "L",
    text: "I am actively investing in someone else's growth — through mentorship, example, or service.",
  },
];

const letterData = {
  S: {
    word: "STILLNESS",
    tagline: "Learning to Subdue Your Passions",
    scripture: "Be still, and know that I am God. — Psalm 46:10",
  },
  I: {
    word: "INTELLIGENCE",
    tagline: "Using Your Mind as Your Primary Weapon",
    scripture:
      "God hath not given us the spirit of fear; but of power, and of love, and of a sound mind. — 2 Timothy 1:7",
  },
  G: {
    word: "GRATITUDE",
    tagline: "Appreciating Small Blessings to Qualify for Bigger Ones",
    scripture: "In every thing give thanks. — 1 Thessalonians 5:18",
  },
  N: {
    word: "NAVIGATION",
    tagline: "Reading Life's Green Lights and Red Lights",
    scripture:
      "Trust in the Lord with all thine heart... and he shall direct thy paths. — Proverbs 3:5-6",
  },
  A: {
    word: "ALIGNMENT",
    tagline: "Fighting Alongside Universal Forces",
    scripture:
      "All things work together for good to them that love God. — Romans 8:28",
  },
  L: {
    word: "LEGACY",
    tagline: "Teaching Others Through Demonstration",
    scripture:
      "A good man leaveth an inheritance to his children's children. — Proverbs 13:22",
  },
};

function getSignal(score: number) {
  if (score >= 9)
    return {
      label: "GREEN LIGHT",
      color: "#27AB1A",
      bg: "#F0FFF0",
      desc: "You are operating at a high level here. Keep flowing.",
    };
  if (score >= 6)
    return {
      label: "YELLOW LIGHT",
      color: "#CC9900",
      bg: "#FFFDF0",
      desc: "You have awareness here but inconsistency is costing you.",
    };
  return {
    label: "RED LIGHT",
    color: "#CC2200",
    bg: "#FFF5F3",
    desc: "This is your greatest opportunity. This is where your work begins.",
  };
}

export default function App() {
  const [page, setPage] = useState("intro"); // intro | quiz | results
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const currentQ = questions[current];
  const answered = Object.keys(answers).length;
  const progress = (answered / questions.length) * 100;

  function selectAnswer(qId: number, val: number) {
    setAnswers((prev) => ({ ...prev, [qId]: val }));
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      if (current < questions.length - 1) {
        setCurrent((c) => c + 1);
      } else {
        setPage("results");
      }
      setAnimating(false);
    }, 320);
  }

  function getScores(): Record<string, number> {
    const scores: Record<string, number> = {};
    Object.keys(letterData).forEach((l) => {
      const qs = questions.filter((q) => q.letter === l);
      scores[l] = qs.reduce((sum, q) => sum + (answers[q.id] || 0), 0);
    });
    return scores;
  }

  // ─── INTRO ────────────────────────────────────────────────────────────────
  if (page === "intro")
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0D1F3C",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "'Georgia', serif",
        }}
      >
        <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            {["S", "I", "G", "N", "A", "L"].map((l, i) => (
              <span
                key={l}
                style={{
                  display: "inline-block",
                  width: 44,
                  height: 44,
                  lineHeight: "44px",
                  background: i % 2 === 0 ? "#CC9900" : "#1A3A6E",
                  color: i % 2 === 0 ? "#0D1F3C" : "#CC9900",
                  fontWeight: "bold",
                  fontSize: 22,
                  marginRight: 4,
                  borderRadius: 4,
                }}
              >
                {l}
              </span>
            ))}
          </div>
          <h1
            style={{
              color: "#CC9900",
              fontSize: "2rem",
              fontWeight: "bold",
              margin: "0 0 0.5rem",
              letterSpacing: 2,
            }}
          >
            SELF-ASSESSMENT
          </h1>
          <p
            style={{
              color: "#B8D0E8",
              fontSize: "1.1rem",
              fontStyle: "italic",
              margin: "0 0 2rem",
            }}
          >
            How to Know When Life Is Saying YES
          </p>
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(204,153,0,0.3)",
              borderRadius: 12,
              padding: "1.5rem 2rem",
              marginBottom: "2rem",
              textAlign: "left",
            }}
          >
            <p
              style={{
                color: "#E8EFF7",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                margin: "0 0 1rem",
              }}
            >
              This assessment will score you across all six letters of the
              S.I.G.N.A.L. Method —{" "}
              <strong style={{ color: "#CC9900" }}>
                Stillness, Intelligence, Gratitude, Navigation, Alignment,
              </strong>{" "}
              and <strong style={{ color: "#CC9900" }}>Legacy</strong>.
            </p>
            <p
              style={{
                color: "#E8EFF7",
                fontSize: "0.95rem",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              Rate each statement honestly from{" "}
              <strong style={{ color: "#CC9900" }}>1 (Almost Never)</strong> to{" "}
              <strong style={{ color: "#CC9900" }}>5 (Almost Always)</strong>.
              Your lowest score is your greatest opportunity.
            </p>
          </div>
          <p
            style={{
              color: "#7A9CBF",
              fontSize: "0.85rem",
              margin: "0 0 2rem",
            }}
          >
            12 questions • Takes about 3 minutes • No wrong answers
          </p>
          <button
            onClick={() => setPage("quiz")}
            style={{
              background: "#CC9900",
              color: "#0D1F3C",
              border: "none",
              borderRadius: 8,
              padding: "1rem 3rem",
              fontSize: "1.1rem",
              fontWeight: "bold",
              cursor: "pointer",
              letterSpacing: 1,
              transition: "transform 0.15s, box-shadow 0.15s",
              boxShadow: "0 4px 20px rgba(204,153,0,0.4)",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 28px rgba(204,153,0,0.6)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(204,153,0,0.4)";
            }}
          >
            Begin Assessment →
          </button>
          <p
            style={{ color: "#4A6A8A", fontSize: "0.8rem", marginTop: "2rem" }}
          >
            Jermaine Green • Greenway International •
            greenscontinuededucation.com
          </p>
        </div>
      </div>
    );

  // ─── QUIZ ─────────────────────────────────────────────────────────────────
  if (page === "quiz") {
    const lData = letterData[currentQ.letter as keyof typeof letterData];
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0D1F3C",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          fontFamily: "'Georgia', serif",
        }}
      >
        <div style={{ maxWidth: 580, width: "100%" }}>
          {/* Progress bar */}
          <div style={{ marginBottom: "1.5rem" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span style={{ color: "#7A9CBF", fontSize: "0.8rem" }}>
                Question {current + 1} of {questions.length}
              </span>
              <span
                style={{
                  color: "#CC9900",
                  fontSize: "0.8rem",
                  fontWeight: "bold",
                }}
              >
                {Math.round(progress)}% complete
              </span>
            </div>
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                borderRadius: 99,
                height: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#CC9900",
                  height: "100%",
                  width: `${progress}%`,
                  borderRadius: 99,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>

          {/* Letter badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                background: "#CC9900",
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <span
                style={{ color: "#0D1F3C", fontSize: 28, fontWeight: "bold" }}
              >
                {currentQ.letter}
              </span>
            </div>
            <div>
              <div
                style={{
                  color: "#CC9900",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  letterSpacing: 2,
                }}
              >
                {lData.word}
              </div>
              <div
                style={{
                  color: "#7A9CBF",
                  fontSize: "0.85rem",
                  fontStyle: "italic",
                }}
              >
                {lData.tagline}
              </div>
            </div>
          </div>

          {/* Question card */}
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(204,153,0,0.25)",
              borderRadius: 14,
              padding: "2rem",
              marginBottom: "1.5rem",
              opacity: animating ? 0.4 : 1,
              transition: "opacity 0.3s",
            }}
          >
            <p
              style={{
                color: "#E8EFF7",
                fontSize: "1.1rem",
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              {currentQ.text}
            </p>
          </div>

          {/* Rating scale labels */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "0.5rem",
              padding: "0 0.25rem",
            }}
          >
            <span style={{ color: "#4A6A8A", fontSize: "0.75rem" }}>
              Almost Never
            </span>
            <span style={{ color: "#4A6A8A", fontSize: "0.75rem" }}>
              Almost Always
            </span>
          </div>

          {/* Rating buttons */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              marginBottom: "2rem",
            }}
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const selected = answers[currentQ.id] === n;
              return (
                <button
                  key={n}
                  onClick={() => selectAnswer(currentQ.id, n)}
                  style={{
                    flex: 1,
                    height: 56,
                    borderRadius: 10,
                    border: selected
                      ? "2px solid #CC9900"
                      : "1px solid rgba(255,255,255,0.15)",
                    background: selected ? "#CC9900" : "rgba(255,255,255,0.04)",
                    color: selected ? "#0D1F3C" : "#B8D0E8",
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    boxShadow: selected
                      ? "0 0 16px rgba(204,153,0,0.5)"
                      : "none",
                  }}
                  onMouseOver={(e) => {
                    if (!selected) {
                      e.currentTarget.style.background = "rgba(204,153,0,0.15)";
                      e.currentTarget.style.borderColor = "rgba(204,153,0,0.5)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!selected) {
                      e.currentTarget.style.background = "rgba(255,255,255,0.04)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                    }
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>

          {/* Letter progress indicators */}
          <div
            style={{ display: "flex", gap: "0.4rem", justifyContent: "center" }}
          >
            {questions.map((q, i) => (
              <div
                key={q.id}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: answers[q.id]
                    ? "#CC9900"
                    : i === current
                    ? "rgba(204,153,0,0.4)"
                    : "rgba(255,255,255,0.1)",
                  transition: "background 0.3s",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS ──────────────────────────────────────────────────────────────
  if (page === "results") {
    const scores = getScores();
    const entries = Object.entries(scores).sort((a, b) => a[1] - b[1]);
    const weakest = entries[0];
    const strongest = entries[entries.length - 1];
    const total = Object.values(scores).reduce((s, v) => s + v, 0);

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0D1F3C",
          padding: "2rem",
          fontFamily: "'Georgia', serif",
        }}
      >
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(204,153,0,0.1)",
                border: "1px solid rgba(204,153,0,0.4)",
                borderRadius: 99,
                padding: "0.4rem 1.2rem",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  color: "#CC9900",
                  fontSize: "0.85rem",
                  letterSpacing: 2,
                  fontWeight: "bold",
                }}
              >
                YOUR RESULTS
              </span>
            </div>
            <h2
              style={{
                color: "#FFFFFF",
                fontSize: "1.8rem",
                margin: "0 0 0.5rem",
                fontWeight: "bold",
              }}
            >
              S.I.G.N.A.L. Breakdown
            </h2>
            <p style={{ color: "#7A9CBF", fontSize: "0.9rem", margin: 0 }}>
              Overall Score:{" "}
              <strong style={{ color: "#CC9900" }}>{total} / 60</strong>
            </p>
          </div>

          {/* Score cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              marginBottom: "2rem",
            }}
          >
            {Object.entries(scores).map(([letter, score]) => {
              const sig = getSignal(score);
              const lData = letterData[letter as keyof typeof letterData];
              const pct = (score / 10) * 100;
              const isWeakest = letter === weakest[0];
              return (
                <div
                  key={letter}
                  style={{
                    background: isWeakest
                      ? "rgba(204,153,0,0.08)"
                      : "rgba(255,255,255,0.04)",
                    border: isWeakest
                      ? "1px solid rgba(204,153,0,0.4)"
                      : "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "1rem 1.25rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "0.6rem",
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        background: sig.color,
                        borderRadius: 6,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          color: "#FFF",
                          fontSize: 20,
                          fontWeight: "bold",
                        }}
                      >
                        {letter}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                        }}
                      >
                        <span
                          style={{
                            color: "#E8EFF7",
                            fontWeight: "bold",
                            fontSize: "0.95rem",
                          }}
                        >
                          {lData.word}
                        </span>
                        <span
                          style={{
                            color: sig.color,
                            fontWeight: "bold",
                            fontSize: "1rem",
                          }}
                        >
                          {score}/10
                        </span>
                      </div>
                      <div
                        style={{
                          color: "#7A9CBF",
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                        }}
                      >
                        {lData.tagline}
                      </div>
                    </div>
                    {isWeakest && (
                      <div
                        style={{
                          background: "#CC9900",
                          color: "#0D1F3C",
                          fontSize: "0.65rem",
                          fontWeight: "bold",
                          padding: "2px 8px",
                          borderRadius: 99,
                          letterSpacing: 1,
                          whiteSpace: "nowrap",
                        }}
                      >
                        START HERE
                      </div>
                    )}
                  </div>
                  {/* Bar */}
                  <div
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      borderRadius: 99,
                      height: 8,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        background: sig.color,
                        height: "100%",
                        width: `${pct}%`,
                        borderRadius: 99,
                        transition: "width 1s ease",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: 6,
                    }}
                  >
                    <span
                      style={{
                        color: sig.color,
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                      }}
                    >
                      {sig.label}
                    </span>
                    <span style={{ color: "#4A6A8A", fontSize: "0.75rem" }}>
                      {sig.desc}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Your greatest signal */}
          <div
            style={{
              background: "rgba(204,153,0,0.08)",
              border: "1px solid #CC9900",
              borderRadius: 14,
              padding: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <div
              style={{
                color: "#CC9900",
                fontWeight: "bold",
                fontSize: "0.85rem",
                letterSpacing: 2,
                marginBottom: "0.5rem",
              }}
            >
              YOUR GREATEST SIGNAL
            </div>
            <p
              style={{
                color: "#E8EFF7",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: "0 0 1rem",
              }}
            >
              Your lowest score is{" "}
              <strong style={{ color: "#CC9900" }}>
                {letterData[weakest[0] as keyof typeof letterData].word}
              </strong>{" "}
              ({weakest[1]}/10). This is not a weakness — this is your next
              door. The S.I.G.N.A.L. Method starts exactly here.
            </p>
            <div
              style={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: 8,
                padding: "0.75rem 1rem",
                borderLeft: "3px solid #CC9900",
              }}
            >
              <p
                style={{
                  color: "#B8D0E8",
                  fontSize: "0.85rem",
                  fontStyle: "italic",
                  margin: 0,
                }}
              >
                {letterData[weakest[0] as keyof typeof letterData].scripture}
              </p>
            </div>
          </div>

          {/* CTA */}
          <div
            style={{
              background: "#1A3A6E",
              borderRadius: 14,
              padding: "1.5rem",
              textAlign: "center",
              marginBottom: "1.5rem",
            }}
          >
            <p
              style={{
                color: "#E8EFF7",
                fontSize: "0.95rem",
                lineHeight: 1.6,
                margin: "0 0 1rem",
              }}
            >
              Ready to go deeper? The full S.I.G.N.A.L. Method course — six
              modules, one letter at a time — is available at Greenway
              International.
            </p>
            <div
              style={{
                color: "#CC9900",
                fontWeight: "bold",
                fontSize: "1rem",
                marginBottom: "0.25rem",
              }}
            >
              greenscontinuededucation.com
            </div>
            <div style={{ color: "#7A9CBF", fontSize: "0.8rem" }}>
              Jermaine Green • Urban Bladez • Greenway International
            </div>
          </div>

          {/* Retake */}
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => {
                setAnswers({});
                setCurrent(0);
                setPage("intro");
              }}
              style={{
                background: "transparent",
                color: "#7A9CBF",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                padding: "0.6rem 1.5rem",
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              ← Retake Assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
